/**
 * sl-oll9.14: CompositeFeatureGates
 *
 * Array-based composite provider that replaces nested provider pyramids.
 * New feature gates are added as array entries instead of JSX nesting.
 *
 * sl-vw37: Vertical gate providers are built dynamically from VERTICAL_DEFINITIONS
 * via createVerticalGateProvider — no hardcoded vertical entries needed.
 *
 * Order matters: providers are applied outside-in (first entry wraps outermost).
 */

// Side-effect import: populates VERTICAL_DEFINITIONS before gate providers are built.
import '@affine/core/modules/streamlain/verticals';

import type { PropsWithChildren, ReactNode } from 'react';

import { VERTICAL_DEFINITIONS } from '../constants/vertical-definitions';
import { ElsterGateProvider } from './elster-gate-provider';
import { FeatureGateProvider } from './feature-gate-provider';
import { TwoFactorGuardProvider } from './two-factor-guard-provider';
import { createVerticalGateProvider } from './vertical-gate-provider-factory';
import { WorkflowGateProvider } from './workflow-gate-provider';

/**
 * Provider component that accepts children and renders ReactNode.
 * Uses a permissive signature to accommodate providers with custom Props interfaces
 * that all follow the same { children } pattern.
 */

type GateProvider = (props: any) => any;

/**
 * Static feature gate providers (non-vertical, hardcoded).
 * Order: FeatureGateProvider (root, fetches plan data) → TwoFactorGuard → per-feature gates.
 */
const STATIC_GATES: GateProvider[] = [
  FeatureGateProvider,
  TwoFactorGuardProvider,
  WorkflowGateProvider,
  ElsterGateProvider,
];

/**
 * Dynamic vertical gate providers built from VERTICAL_DEFINITIONS.
 * Each vertical registers itself via registerVertical() at module init time.
 */
const VERTICAL_GATES: GateProvider[] = VERTICAL_DEFINITIONS.map(def =>
  createVerticalGateProvider(def.featureKey, def.gateClassName)
);

/** All gates: static first, then vertical-dynamic. */
const ALL_GATES = [...STATIC_GATES, ...VERTICAL_GATES];

/**
 * Composes all feature gate providers into a single wrapper.
 * Equivalent to manually nesting them, but scales to any number of gates.
 */
export function CompositeFeatureGates({ children }: PropsWithChildren) {
  return ALL_GATES.reduceRight<ReactNode>(
    (acc, Provider) => <Provider>{acc}</Provider>,
    children
  );
}
